import { ChainStrategy } from "../constants/chain-strategy.js";
import { COUNTDOWN_SECONDS_VALUES, DECISION_CHOICES_MAX, DECISION_CHOICES_MIN } from "../constants/decision.js";
import { FlagConditionCmp, FlagEffectOp, FlagType } from "../constants/flag.js";
import { CLIP_VIDEO_DURATION_VALUES } from "../constants/generation.js";
import { RenderMode } from "../constants/render-mode.js";
import { StoryGraphIssueCode } from "../constants/story-graph-issue.js";
const ALLOWED_COUNTDOWN_SECONDS = new Set(COUNTDOWN_SECONDS_VALUES);
const ALLOWED_VIDEO_DURATIONS = new Set(CLIP_VIDEO_DURATION_VALUES);
export const validateStoryGraph = (graph) => {
    const issues = [];
    const report = (code, message, subjectId = null) => {
        issues.push({ code, message, subjectId });
    };
    const nodesById = new Map();
    for (const node of graph.nodes) {
        if (nodesById.has(node.id)) {
            report(StoryGraphIssueCode.DUPLICATE_NODE_ID, "Identifiant de nœud dupliqué", node.id);
            continue;
        }
        nodesById.set(node.id, node);
    }
    const endingIds = new Set();
    for (const ending of graph.endings) {
        if (endingIds.has(ending.id)) {
            report(StoryGraphIssueCode.DUPLICATE_ENDING_ID, "Identifiant de fin dupliqué", ending.id);
        }
        endingIds.add(ending.id);
    }
    const flagsByName = new Map();
    for (const flag of graph.flags) {
        if (flagsByName.has(flag.name)) {
            report(StoryGraphIssueCode.DUPLICATE_FLAG_NAME, "Nom de flag dupliqué", flag.name);
            continue;
        }
        flagsByName.set(flag.name, flag);
    }
    const shotsByNode = new Map();
    const shotIds = new Set();
    for (const shot of graph.shots) {
        if (shotIds.has(shot.id)) {
            report(StoryGraphIssueCode.DUPLICATE_SHOT_ID, "Identifiant de shot dupliqué", shot.id);
        }
        shotIds.add(shot.id);
        if (!nodesById.has(shot.nodeId)) {
            report(StoryGraphIssueCode.SHOT_NODE_INVALID, "Le shot référence un nœud inconnu", shot.id);
            continue;
        }
        const nodeShots = shotsByNode.get(shot.nodeId);
        if (nodeShots) {
            nodeShots.push(shot);
        }
        else {
            shotsByNode.set(shot.nodeId, [shot]);
        }
    }
    for (const nodeShots of shotsByNode.values()) {
        nodeShots.sort((a, b) => a.orderIndex - b.orderIndex);
    }
    const checkEffects = (effects, subjectId) => {
        for (const effect of effects) {
            const flag = flagsByName.get(effect.flag);
            if (!flag) {
                report(StoryGraphIssueCode.FLAG_UNDECLARED, `Flag non déclaré : ${effect.flag}`, subjectId);
                continue;
            }
            const valid = effect.op === FlagEffectOp.ADD
                ? flag.type === FlagType.COUNTER && typeof effect.value === "number"
                : flag.type === FlagType.BOOLEAN
                    ? typeof effect.value === "boolean"
                    : typeof effect.value === "number";
            if (!valid) {
                report(StoryGraphIssueCode.FLAG_EFFECT_INVALID, `Effet incompatible avec le type du flag ${effect.flag}`, subjectId);
            }
        }
    };
    const checkConditions = (conditions, subjectId) => {
        for (const condition of conditions) {
            const flag = flagsByName.get(condition.flag);
            if (!flag) {
                report(StoryGraphIssueCode.FLAG_UNDECLARED, `Flag non déclaré : ${condition.flag}`, subjectId);
                continue;
            }
            const valid = condition.cmp === FlagConditionCmp.EQ
                ? flag.type === FlagType.BOOLEAN
                    ? typeof condition.value === "boolean"
                    : typeof condition.value === "number"
                : flag.type === FlagType.COUNTER && typeof condition.value === "number";
            if (!valid) {
                report(StoryGraphIssueCode.FLAG_CONDITION_INVALID, `Condition incompatible avec le type du flag ${condition.flag}`, subjectId);
            }
        }
    };
    for (const node of nodesById.values()) {
        const outcomes = [node.decision, node.nextNodeId, node.endingId].filter((outcome) => outcome !== null).length;
        if (outcomes > 1) {
            report(StoryGraphIssueCode.NODE_OUTCOME_CONFLICT, "Issue exclusive : décision, transition directe ou fin", node.id);
        }
        if (node.nextNodeId !== null && !nodesById.has(node.nextNodeId)) {
            report(StoryGraphIssueCode.NODE_REFERENCE_INVALID, "nextNodeId référence un nœud inconnu", node.id);
        }
        if (node.endingId !== null && !endingIds.has(node.endingId)) {
            report(StoryGraphIssueCode.ENDING_REFERENCE_INVALID, "endingId référence une fin inconnue", node.id);
        }
        const nodeShots = shotsByNode.get(node.id) ?? [];
        if (nodeShots.length === 0) {
            report(StoryGraphIssueCode.NODE_WITHOUT_SHOTS, "Le nœud n'a aucun shot", node.id);
        }
        for (const shot of nodeShots) {
            if (shot.durationSeconds <= 0) {
                report(StoryGraphIssueCode.SHOT_DURATION_INVALID, "La durée du shot doit être strictement positive", shot.id);
            }
            else if (shot.renderMode === RenderMode.PREMIUM_VIDEO && !ALLOWED_VIDEO_DURATIONS.has(shot.durationSeconds)) {
                report(StoryGraphIssueCode.SHOT_VIDEO_DURATION_INVALID, "Durée vidéo non supportée par le fournisseur", shot.id);
            }
        }
        if (node.decision !== null) {
            const decision = node.decision;
            if (decision.choices.length < DECISION_CHOICES_MIN || decision.choices.length > DECISION_CHOICES_MAX) {
                report(StoryGraphIssueCode.DECISION_CHOICE_COUNT_INVALID, "Une décision compte 2 à 3 choix", node.id);
            }
            if (!ALLOWED_COUNTDOWN_SECONDS.has(decision.timeoutSeconds)) {
                report(StoryGraphIssueCode.DECISION_TIMEOUT_INVALID, "Durée de countdown non autorisée", node.id);
            }
            const choiceIds = new Set();
            for (const choice of decision.choices) {
                if (choiceIds.has(choice.id)) {
                    report(StoryGraphIssueCode.DUPLICATE_CHOICE_ID, "Identifiant de choix dupliqué", choice.id);
                }
                choiceIds.add(choice.id);
                if (!nodesById.has(choice.nextNodeId)) {
                    report(StoryGraphIssueCode.NODE_REFERENCE_INVALID, "Le choix référence un nœud inconnu", choice.id);
                }
                checkEffects(choice.effects, choice.id);
            }
            if (!choiceIds.has(decision.defaultChoiceId)) {
                report(StoryGraphIssueCode.DECISION_DEFAULT_CHOICE_INVALID, "defaultChoiceId doit référencer un choix de la décision", node.id);
            }
        }
    }
    const parentsByNode = new Map();
    const childrenByNode = new Map();
    const addEdge = (from, to) => {
        if (!nodesById.has(to)) {
            return;
        }
        const parents = parentsByNode.get(to) ?? new Set();
        parents.add(from);
        parentsByNode.set(to, parents);
        const children = childrenByNode.get(from) ?? new Set();
        children.add(to);
        childrenByNode.set(from, children);
    };
    for (const node of nodesById.values()) {
        if (node.nextNodeId !== null) {
            addEdge(node.id, node.nextNodeId);
        }
        if (node.decision !== null) {
            for (const choice of node.decision.choices) {
                addEdge(node.id, choice.nextNodeId);
            }
        }
    }
    const entry = nodesById.get(graph.entryNodeId);
    if (!entry) {
        report(StoryGraphIssueCode.ENTRY_NODE_MISSING, "entryNodeId ne référence aucun nœud du graphe", graph.entryNodeId);
    }
    else if ((parentsByNode.get(entry.id)?.size ?? 0) > 0) {
        report(StoryGraphIssueCode.ENTRY_NODE_HAS_PARENT, "Le nœud d'entrée ne peut pas avoir de parent", entry.id);
    }
    if (entry) {
        const visited = new Set([entry.id]);
        const queue = [entry.id];
        while (queue.length > 0) {
            const currentId = queue.pop();
            if (currentId === undefined) {
                break;
            }
            for (const childId of childrenByNode.get(currentId) ?? []) {
                if (!visited.has(childId)) {
                    visited.add(childId);
                    queue.push(childId);
                }
            }
        }
        for (const node of nodesById.values()) {
            if (!visited.has(node.id)) {
                report(StoryGraphIssueCode.NODE_UNREACHABLE, "Nœud inaccessible depuis l'entrée", node.id);
            }
        }
    }
    const indegree = new Map();
    for (const nodeId of nodesById.keys()) {
        indegree.set(nodeId, 0);
    }
    for (const children of childrenByNode.values()) {
        for (const childId of children) {
            indegree.set(childId, (indegree.get(childId) ?? 0) + 1);
        }
    }
    const kahnQueue = [...nodesById.keys()].filter((nodeId) => (indegree.get(nodeId) ?? 0) === 0);
    let processed = 0;
    while (kahnQueue.length > 0) {
        const nodeId = kahnQueue.pop();
        if (nodeId === undefined) {
            break;
        }
        processed += 1;
        for (const childId of childrenByNode.get(nodeId) ?? []) {
            const remaining = (indegree.get(childId) ?? 0) - 1;
            indegree.set(childId, remaining);
            if (remaining === 0) {
                kahnQueue.push(childId);
            }
        }
    }
    if (processed < nodesById.size) {
        const cycleNodeId = [...nodesById.keys()].find((nodeId) => (indegree.get(nodeId) ?? 0) > 0) ?? null;
        report(StoryGraphIssueCode.GRAPH_CYCLE, "Le graphe contient un cycle", cycleNodeId);
    }
    for (const node of nodesById.values()) {
        const parentCount = parentsByNode.get(node.id)?.size ?? 0;
        const firstShot = (shotsByNode.get(node.id) ?? [])[0];
        if (parentCount >= 2) {
            if (node.convergenceStrategy === null) {
                report(StoryGraphIssueCode.CONVERGENCE_STRATEGY_REQUIRED, "Un nœud de convergence exige une stratégie de convergence", node.id);
            }
            if (firstShot !== undefined && firstShot.chainStrategy !== ChainStrategy.CANONICAL) {
                report(StoryGraphIssueCode.CONVERGENCE_FIRST_SHOT_NOT_CANONICAL, "Le premier shot d'un nœud de convergence doit être en chainStrategy canonical", node.id);
            }
        }
        if (entry !== undefined && node.id === entry.id && firstShot !== undefined && firstShot.chainStrategy === ChainStrategy.INHERIT_TAIL) {
            report(StoryGraphIssueCode.INHERIT_TAIL_WITHOUT_SOURCE, "Le premier shot du nœud d'entrée ne peut pas hériter d'une frame précédente", firstShot.id);
        }
    }
    const ruleEndingIds = new Set();
    const priorities = new Set();
    for (const rule of graph.endingRules) {
        if (endingIds.has(rule.endingId)) {
            ruleEndingIds.add(rule.endingId);
        }
        else {
            report(StoryGraphIssueCode.ENDING_REFERENCE_INVALID, "La règle référence une fin inconnue", rule.id);
        }
        if (priorities.has(rule.priority)) {
            report(StoryGraphIssueCode.RULE_PRIORITY_DUPLICATE, "Priorité de règle dupliquée", rule.id);
        }
        priorities.add(rule.priority);
        checkConditions(rule.conditions, rule.id);
    }
    const defaultRules = graph.endingRules.filter((rule) => rule.conditions.length === 0);
    const conditionalPriorities = graph.endingRules.filter((rule) => rule.conditions.length > 0).map((rule) => rule.priority);
    for (const rule of defaultRules) {
        if (conditionalPriorities.some((priority) => priority <= rule.priority)) {
            report(StoryGraphIssueCode.DEFAULT_RULE_NOT_LOWEST_PRIORITY, "La règle par défaut doit être strictement la moins prioritaire — sinon elle masque les autres règles à l'évaluation", rule.id);
        }
    }
    const staticEndingIds = new Set();
    for (const node of nodesById.values()) {
        if (node.endingId !== null) {
            staticEndingIds.add(node.endingId);
        }
    }
    for (const ending of graph.endings) {
        if (!ruleEndingIds.has(ending.id) && !staticEndingIds.has(ending.id)) {
            report(StoryGraphIssueCode.ENDING_UNRESOLVABLE, "La fin n'a ni règle ni assignation statique", ending.id);
        }
    }
    const hasRuleResolvedTerminal = [...nodesById.values()].some((node) => node.decision === null && node.nextNodeId === null && node.endingId === null);
    if (graph.endingRules.length > 0 || hasRuleResolvedTerminal) {
        if (defaultRules.length === 0) {
            report(StoryGraphIssueCode.DEFAULT_RULE_MISSING, "Une règle par défaut (sans condition) est requise", null);
        }
        else if (defaultRules.length > 1) {
            report(StoryGraphIssueCode.DEFAULT_RULE_DUPLICATE, "Une seule règle par défaut est autorisée", null);
        }
    }
    return issues;
};
